import React, { useEffect, useState } from 'react';
import { Box, Button, Group, Grid, Col, TextInput, Badge, Flex, CloseButton } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import toast from 'react-hot-toast';
import ApiLinks from '../../Navlinks/ApiLinks';
import LoaderOverlay from '../../Utilities/LoadingOverLay';
import { base64DecodeUnicode, base64EncodeUnicode } from '../../Utilities/OtherBasicFunction';

export default function LocationTab({ id, map, landmarks }) {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [iframeHtml, setIframeHtml] = useState('');
  const [showMap, setShowMap] = useState(false);

  const [landmarksList, setLandmarksList] = useState([]);
  const [landmarkInput, setLandmarkInput] = useState('');

  const form = useForm({
    initialValues: {
      propertyid: id || '',
      map: '',
    }
  });

  useEffect(() => {
    if (id) {
      setLoading(true);

      const initializeData = async () => {
        form.setFieldValue('propertyid', id);
        form.setFieldValue('map', map || '');

        if (landmarks) {
          const landmarkArray = landmarks.split(',').map(l => l.trim()).filter(l => l);
          setLandmarksList(landmarkArray);
        }

        if (map) {
          // clean iframe map here also if you want
          let cleanedMap = map.trim();
          if (cleanedMap.includes('<iframe') && cleanedMap.includes('src="https://www.google.com/maps/embed')) {
            cleanedMap = cleanedMap
              .replace(/width="\d+"/g, 'width="100%"')
              .replace(/height="\d+"/g, 'height="100%"');
          }
          setIframeHtml(cleanedMap);
          setShowMap(true);
        }

        setLoading(false); // after all operations done
      };

      initializeData();
    }
  }, [id, user.compcode, map, landmarks]);


  const handleSubmit = async (values) => {
    const landmarksString = landmarksList.join(', ');


    // CLEAN the iframe code before saving
    let cleanedMap = values.map.trim();
    if (cleanedMap.includes('<iframe') && cleanedMap.includes('src="https://www.google.com/maps/embed')) {
      cleanedMap = cleanedMap
        .replace(/width="\d+"/g, 'width="100%"')
        .replace(/height="\d+"/g, 'height="100%"');
    }

    const payload = [
      {
        path: "/map",
        op: "replace",
        value: cleanedMap,
      },
      {
        path: "/landmarks",
        op: "replace",
        value: landmarksString,
      }
    ];

    setLoading(true);

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/${ApiLinks.ProjectOverview_Patch}/${values.propertyid}`, payload
      );

      if (response.data.status) {
        toast.success('Location details saved successfully');
      } else {
        toast.error(response.data.message || 'Failed to save');
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
      toast.error('Error submitting form');
    }
  };

  const handleViewMap = () => {
    let iframeCode = form.values.map.trim();

    if (iframeCode.includes('<iframe') && iframeCode.includes('src="https://www.google.com/maps/embed')) {
      // Remove width and height attributes from iframe
      iframeCode = iframeCode
        .replace(/width="\d+"/g, 'width="100%"')
        .replace(/height="\d+"/g, 'height="100%"');

      setIframeHtml(iframeCode);
      setShowMap(true);
    } else {
      toast.error('Please paste a valid full Google Maps iframe embed code.');
    }
  };


  const handleLandmarkKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = landmarkInput.trim();
      if (value && !landmarksList.includes(value)) {
        setLandmarksList([...landmarksList, value]);
        setLandmarkInput('');
      }
    }
  };

  const handleRemoveLandmark = (removed) => {
    setLandmarksList(landmarksList.filter((item) => item !== removed));
  };

  if (loading) return <LoaderOverlay />;

  return (
    <Box>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Grid gutter="md" Flex="">
          <Col span={12} md={5} >
            <Grid gutter="md">
              {/* Landmarks */}
              <Col span={12}>
                <TextInput
                  label="Locality Landmarks"
                  placeholder="Type city and press Enter or Space..."
                  value={landmarkInput}
                  onChange={(e) => setLandmarkInput(e.currentTarget.value)}
                  onKeyDown={handleLandmarkKeyDown}
                />
                <Flex gap="xs" wrap="wrap" mt="sm">
                  {landmarksList.map((landmark, index) => (
                    <Badge
                      key={index}
                      rightSection={
                        <CloseButton
                          size="xs"
                          onClick={() => handleRemoveLandmark(landmark)}
                          style={{ marginLeft: 5 }}
                        />
                      }
                      variant="light"
                    >
                      {landmark}
                    </Badge>
                  ))}
                </Flex>
              </Col>

              {/* Save / Cancel Buttons */}
              <Col span={12}>
                <Group position="right" mt="md">
                  <Button type="submit">Save</Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/dashboard/floorplans')}
                  >
                    Cancel
                  </Button>
                </Group>
              </Col>

            </Grid>

          </Col>

          <Col span={12} md={6} offset={1} >
            <Grid gutter="md">

              {/* Map Location */}
              <Col span={12}>
                <TextInput
                  label="Map Location"
                  placeholder="Paste full Google Maps iframe code here"
                  {...form.getInputProps('map')}
                />
                <Button
                  mt="sm"
                  variant="outline"
                  onClick={handleViewMap}
                >
                  View Map
                </Button>
              </Col>

              {/* Show Map if Available */}
              {showMap && iframeHtml && (
                <Col span={12}>
                  <Box
                    mt="md"
                    sx={{
                      position: 'relative',
                      width: '100%',
                      paddingBottom: '56.25%', // 16:9 ratio
                      height: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                      }}
                      dangerouslySetInnerHTML={{ __html: iframeHtml }}
                    />
                  </Box>
                </Col>
              )}






            </Grid>

          </Col>
        </Grid>
      </form>
    </Box>
  );
}
